import { useState, useRef, useEffect } from 'react';
import { useAppStore } from '../store';
import { Terminal as TerminalIcon, X, ChevronRight, Square } from 'lucide-react';

export function Terminal() {
    const { terminalLines, clearTerminal, isGenerating } = useAppStore();
    const [showTerminal, setShowTerminal] = useState(false);
    const scrollRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (scrollRef.current) {
            scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
        }
    }, [terminalLines]);

    if (!showTerminal) {
        return (
            <button
                onClick={() => setShowTerminal(true)}
                className="fixed bottom-20 right-6 p-3 rounded-full bg-slate-900 border border-slate-700 shadow-xl hover:border-orange-500 transition-all z-40 group"
            >
                <TerminalIcon className="w-5 h-5 text-slate-400 group-hover:text-orange-500" />
            </button>
        );
    }

    return (
        <div className="fixed bottom-20 right-6 w-[500px] h-[350px] bg-[#0f1114] border border-slate-800 rounded-xl shadow-2xl flex flex-col overflow-hidden z-40 animate-fade-in">
            {/* Header */}
            <div className="h-10 flex items-center justify-between px-4 border-b border-slate-800 bg-slate-900/50">
                <div className="flex items-center gap-2">
                    <TerminalIcon className="w-4 h-4 text-orange-500" />
                    <span className="text-xs font-mono font-medium text-slate-300">Sandbox Terminal</span>
                </div>
                <div className="flex items-center gap-2">
                    <button
                        onClick={clearTerminal}
                        className="p-1 hover:bg-slate-800 rounded text-slate-500 hover:text-slate-300 transition-colors"
                        title="Clear console"
                    >
                        <Square className="w-3 h-3" />
                    </button>
                    <button
                        onClick={() => setShowTerminal(false)}
                        className="p-1 hover:bg-slate-800 rounded text-slate-500 hover:text-slate-300 transition-colors"
                    >
                        <X className="w-4 h-4" />
                    </button>
                </div>
            </div>

            {/* Lines */}
            <div
                ref={scrollRef}
                className="flex-1 overflow-y-auto p-4 font-mono text-[12px] leading-relaxed scroll-smooth bg-[#0a0c0e]"
            >
                {terminalLines.length === 0 ? (
                    <div className="flex flex-col items-center justify-center h-full text-slate-700">
                        <TerminalIcon className="w-8 h-8 mb-2 opacity-20" />
                        <span className="text-xs">Listening for real-time logs...</span>
                    </div>
                ) : (
                    <div className="space-y-1">
                        {terminalLines.map((line, i) => {
                            const isWorker = line.content.includes('[Worker]');
                            const isError = line.stream === 'stderr' || line.content.includes('Error');

                            return (
                                <div key={i} className="flex gap-2 group animate-in slide-in-from-left-1 duration-200">
                                    <span className="text-slate-600 shrink-0 select-none opacity-50">$</span>
                                    <div className="flex flex-col flex-1">
                                        <div className="flex items-center gap-2 mb-0.5">
                                            {isWorker ? (
                                                <span className="text-[10px] px-1.5 py-0.5 rounded bg-orange-500/10 text-orange-500/80 font-bold uppercase tracking-wider">Worker</span>
                                            ) : (
                                                <span className="text-[10px] px-1.5 py-0.5 rounded bg-blue-500/10 text-blue-500/80 font-bold uppercase tracking-wider">Sandbox</span>
                                            )}
                                            <span className="text-[10px] text-slate-600 font-medium">{new Date().toLocaleTimeString([], { hour12: false, hour: '2-digit', minute: '2-digit', second: '2-digit' })}</span>
                                        </div>
                                        <span className={`break-all ${isError ? 'text-rose-400' : 'text-slate-300'}`}>
                                            {line.content.replace('[Worker] ', '').replace('[Worker Error] ', '')}
                                        </span>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
                {isGenerating && (
                    <div className="flex items-center gap-2 text-orange-500/80 mt-4 px-2 py-1 rounded bg-orange-500/5 w-fit animate-pulse">
                        <div className="flex gap-1">
                            <span className="w-1.5 h-1.5 rounded-full bg-orange-500 animate-bounce [animation-delay:-0.3s]" />
                            <span className="w-1.5 h-1.5 rounded-full bg-orange-500 animate-bounce [animation-delay:-0.15s]" />
                            <span className="w-1.5 h-1.5 rounded-full bg-orange-500 animate-bounce" />
                        </div>
                        <span className="text-xs font-bold uppercase tracking-tighter">AI Processing</span>
                    </div>
                )}
            </div>
        </div>
    );
}
